import { Anchor, Loader, Stack, Text, Title } from '@mantine/core'
import type { PermissionScope, Resource } from '@tnn/core'
import { accessBy, givesEverything, role, user } from '@tnn/domain'
import { useTranslation } from '@tnn/i18n'
import { useLinkComponent } from '@tnn/ui'
import { type ReactNode, useMemo } from 'react'
import { useResourceList } from '../api/resources'
import { appLink } from '../app/links'
import { useCan } from '../auth/session'
import { PersonAvatar } from '../photo/photos'
import classes from './access.module.css'

/**
 * Who may do what (docs/permissions.md): the roles page of Admin, its pages by app and resource, and the guide of every
 * page. Roles are read as the database has them and as the API enforces them (`accessBy`, `rightsOf`), so what is shown
 * is what is allowed. Only for whoever may read roles; the people holding them for whoever may read accounts.
 */

/** A right as a chip (an action, a role, everyone); one that reaches fewer than all records says which in a tag */
export function RightChip({ label, reach = 'all', href }: { label: ReactNode; reach?: PermissionScope; href?: string }) {
  const { t } = useTranslation()
  const Link = useLinkComponent()
  const Root = (href ? Link : 'span') as 'a'
  return (
    <Root href={href} className={classes.chip} data-link={href ? true : undefined}>
      {label}
      {reach !== 'all' && (
        <span className={classes.reach} data-reach={reach}>
          {t(`admin:role.rights.${reach}`)}
        </span>
      )}
    </Root>
  )
}

/** Rows of rights: a label (a resource, an action) with its chips beside it, stacked where there is no room */
export function RightRows({ rows }: { rows: { key: string; label: ReactNode; chips: ReactNode }[] }) {
  return (
    <dl className={classes.rows}>
      {rows.map((row) => (
        <div key={row.key} className={classes.row}>
          <dt>{row.label}</dt>
          <dd>{row.chips}</dd>
        </div>
      ))}
    </dl>
  )
}

/** What the tags of the chips above mean, those shown only */
export function ReachLegend({ reaches }: { reaches: Iterable<PermissionScope> }) {
  const { t } = useTranslation()
  const shown = new Set(reaches)
  return (['branch', 'team', 'own'] as const)
    .filter((reach) => shown.has(reach))
    .map((reach) => (
      <Text key={reach} size="sm" c="dimmed" className={classes.legend}>
        <span className={classes.reach} data-reach={reach}>
          {t(`admin:role.rights.${reach}`)}
        </span>
        {t(`admin:role.rights.${reach}Hint`)}
      </Text>
    ))
}

/** People as chips, wrapping */
export function PersonChips({ children }: { children: ReactNode }) {
  return <div className={classes.people}>{children}</div>
}

/** A person as a chip, to their account in Admin */
export function PersonChip({ id, name }: { id: string; name: string }) {
  const Link = useLinkComponent()
  return (
    <Anchor component={Link} href={appLink('admin', `/users/${id}`)} className={classes.person} underline="never">
      <PersonAvatar name={name} userId={id} size={28} />
      <span>{name}</span>
    </Anchor>
  )
}

export interface RosterRole {
  id: string
  key: string
  name: string
  permissions: string[]
}

/**
 * The roles, and the open accounts with the roles each holds: each only for whoever may read it (`admin.role`,
 * `admin.user`), else undefined. One query each, shared by every page and guide that shows them
 */
export function useRoster() {
  const can = useCan()
  const seesRoles = can('read', role.key)
  const seesUsers = can('read', user.key)
  const roles = useResourceList(role, { pageSize: 100, sort: 'name' }, seesRoles)
  const users = useResourceList(user, { pageSize: 1000, sort: 'name' }, seesUsers)
  return useMemo(
    () => ({
      seesRoles,
      roles: roles.data?.items.map(
        (r): RosterRole => ({ id: r.id, key: String(r.key), name: String(r.name), permissions: (r.permissions as string[]) ?? [] }),
      ),
      users: users.data?.items
        .filter((u) => u.active !== false)
        .map((u) => ({ id: u.id, name: String(u.name), roleIds: (u.roleIds as string[]) ?? [] })),
    }),
    [seesRoles, roles.data, users.data],
  )
}

/** Who may do what with the roles of the roster: `accessBy` once per load of the roles */
export function useAccess(roles: RosterRole[] | undefined) {
  return useMemo(() => roles && accessBy(roles), [roles])
}

/** How many people of a role are shown before "and N more" */
const SHOWN_PEOPLE = 12

/**
 * Who may do what with a resource: each action it offers and who may do it (everyone, or each role and how far), then
 * the people holding those roles. Nothing for someone who may not read roles
 */
export function ResourceAccess({ resource }: { resource: Resource }) {
  const { t } = useTranslation()
  const Link = useLinkComponent()
  const roster = useRoster()
  const access = useAccess(roster.roles)
  if (!roster.seesRoles) return null
  if (!roster.roles || !access) return <Loader size="sm" />
  const all = roster.roles.filter((r) => givesEverything(r.permissions))
  const allNames = all.map((r) => r.name).join(', ')
  const rows = resource.actions.map((action) => ({ action, ...access(resource.key, action) }))
  const holding = roster.roles.filter((r) => all.includes(r) || rows.some((row) => row.roles.some((given) => given.role === r)))
  return (
    <Stack gap="xl">
      <Stack gap={8}>
        <Title order={4}>{t('admin:access.title')}</Title>
        {allNames && (
          <Text size="sm" c="dimmed">
            {t('admin:access.everything', { roles: allNames })}
          </Text>
        )}
        <div className={classes.box}>
          <RightRows
            rows={rows.map(({ action, everyone, team, roles }) => ({
              key: action,
              label: t(`admin:actions.${action}`, { defaultValue: action }),
              chips:
                everyone || team || roles.length ? (
                  <>
                    {everyone && <RightChip label={t('admin:access.everyone')} reach={everyone} />}
                    {team && <RightChip label={t('admin:access.team')} reach={team} />}
                    {roles.map((given) => (
                      <RightChip
                        key={given.role.id}
                        label={given.lead ? t('admin:access.lead', { role: given.role.name }) : given.role.name}
                        reach={given.reach}
                        href={appLink('admin', `/roles/${given.role.id}`)}
                      />
                    ))}
                  </>
                ) : (
                  <Text size="sm" c="dimmed" lh="28px">
                    {allNames ? t('admin:access.only', { roles: allNames }) : '–'}
                  </Text>
                ),
            }))}
          />
        </div>
        <ReachLegend
          reaches={rows.flatMap((row) => [
            ...(row.everyone ? [row.everyone] : []),
            ...(row.team ? [row.team] : []),
            ...row.roles.map((r) => r.reach),
          ])}
        />
      </Stack>
      {roster.users && (
        <Stack gap="md">
          <Title order={4}>{t('admin:access.users')}</Title>
          {holding.map((r) => {
            const people = roster.users?.filter((u) => u.roleIds.includes(r.id)) ?? []
            return (
              <Stack key={r.id} gap={6}>
                <Text size="sm" fw={500}>
                  {`${r.name} · ${t('admin:role.people', { count: people.length })}`}
                </Text>
                {people.length > 0 && (
                  <PersonChips>
                    {people.slice(0, SHOWN_PEOPLE).map((person) => (
                      <PersonChip key={person.id} id={person.id} name={person.name} />
                    ))}
                    {people.length > SHOWN_PEOPLE && (
                      <Anchor component={Link} href={appLink('admin', `/users?f.roleIds=${r.id}`)} size="sm" className={classes.more}>
                        {t('admin:access.more', { count: people.length - SHOWN_PEOPLE })}
                      </Anchor>
                    )}
                  </PersonChips>
                )}
              </Stack>
            )
          })}
        </Stack>
      )}
    </Stack>
  )
}
